import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { formatINR } from '@/lib/utils';
import { useAuth } from '@/lib/auth';

const empty = {
  name: '', description: '', duration_days: 30, price: 999, discount_percent: 5,
  perks_text: '', active: true, highlight: false,
  is_team: false, team_size: 1, allows_slot_reservation: false, sport_id: '',
};

export default function AdminPlans() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [sports, setSports] = useState([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const load = () => api.get('/plans', { params: { only_active: false } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get('/sports').then((r) => setSports(r.data)).catch(() => {});
    // See AdminCourts.jsx — same venue-picker rule.
    if (isSuperAdmin) {
      api.get('/admin/academies').then((r) => setVenues(r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const sportName = (id) => sports.find((s) => s.id === id)?.name;

  const openNew = () => { setForm({ ...empty, sport_id: sports[0]?.id || '' }); setEditing(null); setVenueId(''); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p, perks_text: (p.perks || []).join('\n') }); setEditing(p.id); setOpen(true); };

  const save = async () => {
    if (!editing && isSuperAdmin && venues.length > 1 && !venueId) {
      toast({ title: 'Choose a venue', description: 'More than one venue exists — pick which one this plan belongs to', variant: 'error' });
      return;
    }
    try {
      const payload = {
        name: form.name,
        description: form.description,
        duration_days: Number(form.duration_days),
        price: Number(form.price),
        discount_percent: Number(form.discount_percent),
        perks: form.perks_text.split('\n').map((s) => s.trim()).filter(Boolean),
        active: !!form.active,
        highlight: !!form.highlight,
        is_team: !!form.is_team,
        team_size: Math.max(1, Number(form.team_size) || 1),
        allows_slot_reservation: !!form.allows_slot_reservation,
        sport_id: form.sport_id || undefined,
      };
      if (editing) await api.put(`/plans/${editing}`, payload);
      else await api.post('/plans', payload, { params: venueId ? { academy_id: venueId } : {} });
      toast({ title: editing ? 'Plan updated' : 'Plan added', variant: 'success' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail || '', variant: 'error' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this plan? Existing memberships will stay valid.')) return;
    try { await api.delete(`/plans/${id}`); toast({ title: 'Plan deleted', variant: 'success' }); load(); }
    catch { toast({ title: 'Delete failed', variant: 'error' }); }
  };

  return (
    <div className="space-y-6" data-testid="admin-plans">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Memberships</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Plans</h1>
        </div>
        <Button onClick={openNew} data-testid="add-plan-button"><Plus size={14} /> Add Plan</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className={`bg-surface border ${p.highlight ? 'border-volt volt-glow' : 'border-white/10'} p-6 flex flex-col`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <Badge tone={p.active ? 'success' : 'default'}>{p.active ? 'Active' : 'Hidden'}</Badge>
              <div className="flex gap-1">
                {sportName(p.sport_id) && <Badge tone="volt">{sportName(p.sport_id)}</Badge>}
                {p.is_team && <Badge tone="volt">Team · {p.team_size}</Badge>}
                {p.allows_slot_reservation && <Badge>Slot Lock</Badge>}
                {p.highlight && <Badge tone="volt">Featured</Badge>}
              </div>
            </div>
            <h3 className="font-heading font-black text-2xl uppercase">{p.name}</h3>
            <p className="text-xs text-white/50 mt-1 mb-4">{p.duration_days} day pass · {p.discount_percent}% off bookings</p>
            <div className="font-heading font-black text-4xl text-white mb-4">{formatINR(p.price)}</div>
            <ul className="space-y-2 mb-4 flex-1 text-sm text-white/70">
              {(p.perks || []).slice(0, 3).map((perk) => (<li key={perk} className="flex gap-2"><span className="text-volt">▸</span> <span className="truncate">{perk}</span></li>))}
            </ul>
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}><Edit3 size={12} /> Edit</Button>
              <Button variant="danger" size="sm" onClick={() => remove(p.id)} data-testid={`delete-plan-${p.id}`}><Trash2 size={12} /></Button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="plan-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">{editing ? 'Edit' : 'New'} Plan</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {!editing && isSuperAdmin && venues.length > 1 && (
                <Field label="Venue">
                  <select
                    value={venueId}
                    onChange={(e) => setVenueId(e.target.value)}
                    className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                    data-testid="plan-venue"
                  >
                    <option value="">Choose a venue…</option>
                    {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                  </select>
                </Field>
              )}
              <Field label="Sport" hint="This plan's discount and reserved-slot lock only ever apply to this sport's courts.">
                <select
                  value={form.sport_id || ''}
                  onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
                  className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                  data-testid="plan-sport"
                >
                  <option value="">Choose a sport…</option>
                  {sports.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="Plan Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pro" data-testid="plan-name" /></Field>
              <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Days"><Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} data-testid="plan-days" /></Field>
                <Field label="Price (₹)"><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="plan-price" /></Field>
                <Field label="Discount %"><Input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="plan-discount" /></Field>
              </div>
              <Field label="Perks (one per line)">
                <Textarea
                  value={form.perks_text}
                  onChange={(e) => setForm({ ...form, perks_text: e.target.value })}
                  placeholder="15% off all bookings&#10;Priority booking window&#10;1 free coaching session/month"
                  className="min-h-[120px]"
                  data-testid="plan-perks"
                />
              </Field>
              <div className="flex flex-col gap-3 pt-2 border-t border-white/10">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input type="checkbox" checked={!!form.is_team} onChange={(e) => setForm({ ...form, is_team: e.target.checked })} className="accent-volt" data-testid="plan-is-team" />
                  Team plan (members can bring teammates)
                </label>
                {form.is_team && (
                  <Field label="Players per court (team size)">
                    <Input type="number" min="2" max="20" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} data-testid="plan-team-size" />
                  </Field>
                )}
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input type="checkbox" checked={!!form.allows_slot_reservation} onChange={(e) => setForm({ ...form, allows_slot_reservation: e.target.checked })} className="accent-volt" data-testid="plan-allows-slot" />
                  Lock a daily court slot (asks member at checkout)
                </label>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-volt" /> Active
                </label>
                <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                  <input type="checkbox" checked={!!form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} className="accent-volt" /> Highlight as featured
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} className="flex-1" data-testid="save-plan">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
